import type { RoomDetail, RoomModerationState, RoomTheme } from '@crowdset/contracts';
import { API, ROOM_THEMES } from '@crowdset/contracts/constants';
import { THEME_META } from '@crowdset/ui';
import { Archive, Copy, LinkSimple, SignOut, Star, Trash } from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { api, errorMessage } from '../../lib/api';
import {
  useCreateInvite,
  useFavoriteToggle,
  useHistory,
  useInvites,
  useRevokeInvite,
  useSession,
} from '../../lib/queries';
import { useCommand, useConnection } from '../../room/context';
import { useRoomStore } from '../../room/store';
import {
  Badge,
  Button,
  cx,
  Dialog,
  EmptyState,
  Field,
  Input,
  Select,
  Textarea,
  timeAgo,
} from '../../ui/kit';
import { toast } from '../Toasts';

export function RoomInfo() {
  const data = useRoomStore((s) => s.data)!;
  const session = useSession().data!;
  const conn = useConnection();
  const nav = useNavigate();
  const fav = useFavoriteToggle();
  const { room } = data;
  const isOwner = room.myRole === 'owner';
  const isMod = isOwner || room.myRole === 'moderator';
  const [section, setSection] = useState<
    'about' | 'history' | 'settings' | 'invites' | 'moderation'
  >('about');

  const tabs = [
    ['about', 'About'],
    ['history', 'History'],
    ...(isOwner ? [['settings', 'Settings'] as const] : []),
    ...(isOwner && room.visibility === 'private' ? [['invites', 'Invites'] as const] : []),
    ...(isMod ? [['moderation', 'Bans & mutes'] as const] : []),
  ] as [typeof section, string][];

  return (
    <div className="cs-scroll flex h-full flex-col overflow-y-auto">
      <div
        className="flex gap-1 border-b border-white/[0.06] px-3 pt-3"
        role="tablist"
        aria-label="Room information sections"
      >
        {tabs.map(([k, label]) => (
          <button
            key={k}
            role="tab"
            aria-selected={section === k}
            onClick={() => setSection(k)}
            className={cx(
              'rounded-t-lg px-3 py-2 text-xs',
              section === k ? 'bg-white/[0.06] text-fg' : 'text-fg-muted hover:text-fg',
            )}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="p-4">
        {section === 'about' && (
          <div className="flex flex-col gap-4">
            <div>
              <h3 className="text-lg font-semibold tracking-tight">{room.name}</h3>
              {room.description && (
                <p className="mt-1 text-sm leading-relaxed text-fg-muted">{room.description}</p>
              )}
              <div className="mt-2 flex flex-wrap gap-1.5">
                <Badge>{room.visibility === 'private' ? 'Private' : 'Public'}</Badge>
                <Badge>{THEME_META[room.theme].name}</Badge>
                {room.genreTags.map((t) => (
                  <Badge key={t}>{t}</Badge>
                ))}
              </div>
            </div>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <dt className="text-fg-subtle">Owner</dt>
              <dd>{room.owner?.displayName ?? 'Archived owner'}</dd>
              <dt className="text-fg-subtle">Moderators</dt>
              <dd>
                {room.moderators.length
                  ? room.moderators.map((m) => m.displayName).join(', ')
                  : 'None'}
              </dd>
              <dt className="text-fg-subtle">Capacity</dt>
              <dd className="font-mono">{room.settings.capacity}</dd>
              <dt className="text-fg-subtle">Max track length</dt>
              <dd className="font-mono">{Math.round(room.settings.maxSongMs / 60000)} min</dd>
              <dt className="text-fg-subtle">Vote skip</dt>
              <dd>{room.settings.voteSkipEnabled ? '3+ Mehs and 60% after 30 s' : 'Off'}</dd>
              <dt className="text-fg-subtle">Slow mode</dt>
              <dd>
                {room.settings.slowModeSeconds ? `${room.settings.slowModeSeconds} s` : 'Off'}
              </dd>
              <dt className="text-fg-subtle">Your role</dt>
              <dd className="capitalize">{room.myRole}</dd>
            </dl>
            <div className="flex flex-wrap gap-2">
              {session.user && (
                <Button
                  size="sm"
                  onClick={() => fav.mutate({ roomId: room.id, on: !room.isFavorite })}
                  aria-pressed={room.isFavorite}
                >
                  <Star
                    size={14}
                    weight={room.isFavorite ? 'fill' : 'bold'}
                    className={room.isFavorite ? 'text-woot' : ''}
                    aria-hidden
                  />
                  {room.isFavorite ? 'Saved' : 'Save room'}
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={async () => {
                  await conn.leave();
                  nav('/');
                }}
              >
                <SignOut size={14} weight="bold" aria-hidden /> Leave room
              </Button>
            </div>
            <p className="text-xs leading-relaxed text-fg-subtle">
              Woots and Mehs are CrowdSet reactions only. They are not YouTube likes and do not
              count as YouTube views.
            </p>
          </div>
        )}
        {section === 'history' && <HistoryList slug={room.slug} />}
        {section === 'settings' && isOwner && <RoomSettingsForm room={room} />}
        {section === 'invites' && isOwner && <Invites slug={room.slug} />}
        {section === 'moderation' && isMod && <ModerationLists slug={room.slug} />}
      </div>
    </div>
  );
}

function HistoryList({ slug }: { slug: string }) {
  const h = useHistory(slug, true);
  const items = h.data?.pages.flatMap((p) => p.items) ?? [];
  if (h.isPending) return <p className="text-sm text-fg-muted">Loading history…</p>;
  if (h.isError) return <p className="text-sm text-[#f3a3a4]">{errorMessage(h.error)}</p>;
  if (items.length === 0) return <EmptyState title="Nothing has played here yet" />;
  return (
    <div>
      <ol className="flex flex-col gap-2">
        {items.map((e) => (
          <li key={e.playId} className="rounded-xl border border-white/[0.06] p-3">
            <p className="truncate text-sm font-medium">{e.video.title}</p>
            <p className="truncate text-xs text-fg-muted">
              {e.dj?.displayName ?? 'Former user'} · {timeAgo(e.startedAt)}
              {e.endReason && e.endReason !== 'completed' && (
                <span className="text-warn"> · {e.endReason.replace(/_/g, ' ')}</span>
              )}
            </p>
            <p className="mt-1 font-mono text-[11px] text-fg-subtle">
              <span className="text-woot">{e.woots} woot</span> ·{' '}
              <span className="text-meh">{e.mehs} meh</span>
            </p>
          </li>
        ))}
      </ol>
      {h.hasNextPage && (
        <Button
          size="sm"
          variant="ghost"
          className="mt-3"
          onClick={() => void h.fetchNextPage()}
          loading={h.isFetchingNextPage}
        >
          Older plays
        </Button>
      )}
    </div>
  );
}

function RoomSettingsForm({ room }: { room: RoomDetail }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [name, setName] = useState(room.name);
  const [description, setDescription] = useState(room.description);
  const [theme, setTheme] = useState<RoomTheme>(room.theme);
  const [capacity, setCapacity] = useState(String(room.settings.capacity));
  const [maxMin, setMaxMin] = useState(String(Math.round(room.settings.maxSongMs / 60000)));
  const [voteSkip, setVoteSkip] = useState(room.settings.voteSkipEnabled);
  const [slow, setSlow] = useState(String(room.settings.slowModeSeconds));
  const [tags, setTags] = useState(room.genreTags.join(', '));
  const [confirmArchive, setConfirmArchive] = useState(false);
  const save = useMutation({
    mutationFn: () =>
      api.patch(API.room(room.slug), {
        name,
        description,
        theme,
        capacity: Number(capacity),
        maxSongMinutes: Number(maxMin),
        voteSkipEnabled: voteSkip,
        slowModeSeconds: Number(slow),
        genreTags: tags
          .split(',')
          .map((t) => t.trim().toLowerCase())
          .filter(Boolean)
          .slice(0, 5),
      }),
    onSuccess: () => {
      toast('Room settings saved.', 'ok');
      void qc.invalidateQueries({ queryKey: ['room', room.slug] });
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const archive = useMutation({
    mutationFn: () => api.post(API.roomArchive(room.slug)),
    onSuccess: () => {
      toast('Room archived.', 'ok');
      nav('/');
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <Field label="Name">
        {(id) => (
          <Input
            id={id}
            value={name}
            minLength={3}
            maxLength={60}
            onChange={(e) => setName(e.target.value)}
          />
        )}
      </Field>
      <Field label="Description">
        {(id) => (
          <Textarea
            id={id}
            value={description}
            maxLength={280}
            onChange={(e) => setDescription(e.target.value)}
          />
        )}
      </Field>
      <Field label="Genre tags" hint="Comma separated, up to 5.">
        {(id, d) => (
          <Input
            id={id}
            aria-describedby={d}
            value={tags}
            onChange={(e) => setTags(e.target.value)}
          />
        )}
      </Field>
      <Field label="Theme">
        {(id) => (
          <Select
            id={id}
            value={theme}
            onChange={(v) => setTheme(v as RoomTheme)}
            options={ROOM_THEMES.map((t) => ({ value: t, label: THEME_META[t].name }))}
          />
        )}
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Capacity">
          {(id) => (
            <Input
              id={id}
              type="number"
              min={2}
              max={500}
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
            />
          )}
        </Field>
        <Field label="Max track (min)" hint="Server cap applies.">
          {(id, d) => (
            <Input
              id={id}
              aria-describedby={d}
              type="number"
              min={1}
              max={60}
              value={maxMin}
              onChange={(e) => setMaxMin(e.target.value)}
            />
          )}
        </Field>
        <Field label="Slow mode (s)">
          {(id) => (
            <Input
              id={id}
              type="number"
              min={0}
              max={300}
              value={slow}
              onChange={(e) => setSlow(e.target.value)}
            />
          )}
        </Field>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={voteSkip}
          onChange={(e) => setVoteSkip(e.target.checked)}
          className="h-4 w-4 accent-[#5fb7c9]"
        />
        Auto-skip on negative votes (3+ Mehs and 60% after 30 s)
      </label>
      <div className="flex justify-between gap-2">
        <Button variant="danger" size="sm" onClick={() => setConfirmArchive(true)}>
          <Archive size={14} weight="bold" aria-hidden /> Archive room
        </Button>
        <Button type="submit" variant="primary" loading={save.isPending}>
          Save settings
        </Button>
      </div>
      <Dialog
        open={confirmArchive}
        onOpenChange={setConfirmArchive}
        title="Archive this room?"
        description="Everyone is disconnected, the current track ends, and the room disappears from discovery. History is kept."
      >
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setConfirmArchive(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => archive.mutate()} loading={archive.isPending}>
            Archive room
          </Button>
        </div>
      </Dialog>
    </form>
  );
}

function Invites({ slug }: { slug: string }) {
  const invites = useInvites(slug, true);
  const create = useCreateInvite(slug);
  const revoke = useRevokeInvite(slug);
  const [hours, setHours] = useState('72');
  const [maxUses, setMaxUses] = useState('');
  const [fresh, setFresh] = useState<string | null>(null);

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast('Invite link copied.', 'ok');
    } catch {
      toast('Copy failed — select the link and copy it manually.', 'warn');
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-fg-muted">
        Invite links expire and can be revoked. Only people with an account can accept them.
      </p>
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate(
            { expiresInHours: Number(hours), maxUses: maxUses ? Number(maxUses) : null },
            {
              onSuccess: (r) => setFresh(`${window.location.origin}${r.path}`),
              onError: (err) => toast(errorMessage(err), 'error'),
            },
          );
        }}
      >
        <div className="grid grid-cols-2 gap-3">
          <Field label="Expires after">
            {(id) => (
              <Select
                id={id}
                value={hours}
                onChange={setHours}
                options={[
                  { value: '1', label: '1 hour' },
                  { value: '24', label: '1 day' },
                  { value: '72', label: '3 days' },
                  { value: '168', label: '7 days' },
                  { value: '720', label: '30 days' },
                ]}
              />
            )}
          </Field>
          <Field label="Max uses" hint="Blank = unlimited">
            {(id, d) => (
              <Input
                id={id}
                aria-describedby={d}
                type="number"
                min={1}
                max={500}
                value={maxUses}
                onChange={(e) => setMaxUses(e.target.value)}
              />
            )}
          </Field>
        </div>
        <Button type="submit" variant="primary" loading={create.isPending}>
          <LinkSimple size={14} weight="bold" aria-hidden /> Create invite link
        </Button>
      </form>
      {fresh && (
        <div className="rounded-xl border border-accent/30 bg-accent/[0.06] p-3">
          <p className="mb-2 text-xs text-fg-muted">
            Copy it now — for security it is shown only once.
          </p>
          <div className="flex gap-2">
            <Input
              readOnly
              value={fresh}
              onFocus={(e) => e.currentTarget.select()}
              aria-label="New invite link"
              className="font-mono text-xs"
            />
            <Button onClick={() => void copy(fresh)} aria-label="Copy invite link">
              <Copy size={14} weight="bold" aria-hidden />
            </Button>
          </div>
        </div>
      )}
      <ul className="flex flex-col gap-2">
        {invites.data?.map((i) => {
          const expired = Date.parse(i.expiresAt) < Date.now();
          const state = i.revokedAt
            ? 'Revoked'
            : expired
              ? 'Expired'
              : i.maxUses !== null && i.uses >= i.maxUses
                ? 'Used up'
                : 'Active';
          return (
            <li
              key={i.id}
              className="flex items-center gap-3 rounded-xl border border-white/[0.06] p-3 text-sm"
            >
              <div className="min-w-0 flex-1">
                <p>
                  <Badge tone={state === 'Active' ? 'ok' : 'neutral'}>{state}</Badge>{' '}
                  <span className="font-mono text-xs text-fg-muted">
                    {i.uses}
                    {i.maxUses !== null ? `/${i.maxUses}` : ''} uses
                  </span>
                </p>
                <p className="mt-1 text-xs text-fg-subtle">
                  Expires {new Date(i.expiresAt).toLocaleString()}
                </p>
              </div>
              {state === 'Active' && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => revoke.mutate(i.id)}
                  aria-label="Revoke invite"
                >
                  <Trash size={14} weight="bold" aria-hidden /> Revoke
                </Button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Current bans and active mutes, with the actions that lift them (moderators only). */
function ModerationLists({ slug }: { slug: string }) {
  const qc = useQueryClient();
  const key = ['room-moderation', slug];
  const q = useQuery({
    queryKey: key,
    queryFn: () => api.get<RoomModerationState>(API.roomModeration(slug)),
    refetchInterval: 15_000,
  });
  const unban = useCommand('moderation.unban', { success: 'Ban lifted.' });
  const unmute = useCommand('moderation.unmute', { success: 'Mute lifted.' });
  const refresh = () => void qc.invalidateQueries({ queryKey: key });
  if (q.isPending) return <p className="text-sm text-fg-muted">Loading…</p>;
  if (q.isError) return <p className="text-sm text-[#f3a3a4]">{errorMessage(q.error)}</p>;
  const until = (ms: number | null) =>
    ms === null ? 'permanent' : `until ${new Date(ms).toLocaleString()}`;
  return (
    <div className="flex flex-col gap-6">
      <section aria-labelledby="bans-h">
        <h3 id="bans-h" className="mb-2 text-sm font-medium">
          Banned ({q.data.bans.length})
        </h3>
        {q.data.bans.length === 0 ? (
          <p className="text-sm text-fg-muted">Nobody is banned from this room.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {q.data.bans.map((b) => (
              <li
                key={b.user.id}
                className="flex items-center gap-3 rounded-xl border border-white/[0.06] p-3"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">
                    {b.user.displayName}{' '}
                    <span className="font-mono text-xs text-fg-subtle">@{b.user.handle}</span>
                  </p>
                  <p className="truncate text-xs text-fg-muted">
                    {b.reason} · {until(b.untilMs)}
                    {b.bannedBy ? ` · by ${b.bannedBy.displayName}` : ''}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  loading={unban.pending}
                  onClick={async () => {
                    const ack = await unban.run({ userId: b.user.id });
                    if (ack.ok) refresh();
                  }}
                >
                  Unban
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>
      <section aria-labelledby="mutes-h">
        <h3 id="mutes-h" className="mb-2 text-sm font-medium">
          Muted ({q.data.mutes.length})
        </h3>
        {q.data.mutes.length === 0 ? (
          <p className="text-sm text-fg-muted">Nobody is muted right now.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {q.data.mutes.map((m) => (
              <li
                key={m.user.id}
                className="flex items-center gap-3 rounded-xl border border-white/[0.06] p-3"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{m.user.displayName}</p>
                  <p className="truncate text-xs text-fg-muted">
                    {m.reason || 'No reason given'} · {until(m.untilMs)}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  loading={unmute.pending}
                  onClick={async () => {
                    const ack = await unmute.run({ userId: m.user.id });
                    if (ack.ok) refresh();
                  }}
                >
                  Lift mute
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
